import { shallowRef, triggerRef, Ref, computed, ComputedRef, watch } from 'vue';

interface CacheEntry<T> {
  timestamp: number;
  data: T;
}

abstract class CacheMap<T> {
  private map = shallowRef(new Map<string, CacheEntry<T>>());
  private beforeDeletionCallbacks: ((thisObj: this) => void)[] = [];
  private intervalId?: number;

  constructor(updateFrequency: number | null) {
    if (updateFrequency === null)
      return;

    this.intervalId = window.setInterval(() => {
      for (const callback of this.beforeDeletionCallbacks) {
        callback(this);
      }

      this.deleteOutdatedEntries()
    }, updateFrequency);
  }

  public get size() {
    return this.map.value.size;
  }

  public get(key: string, shouldRefreshTimestampForKey: boolean = false): T | undefined {
    if (!key)
      return undefined;

    const cacheValue = this.map.value.get(key);

    if (!cacheValue)
      return undefined;

    if (shouldRefreshTimestampForKey)
      this.refreshTimestampForKey(key);

    return cacheValue.data;
  }

  public getAllMatchingKeys(value: T, compareFn: (a: T, b: T) => boolean): string[] {
    const matchingKeys: string[] = [];

    for (const [cacheKey, cacheValue] of this.map.value.entries()) {
      if (compareFn(value, cacheValue.data)) {
        matchingKeys.push(cacheKey);
      }
    }

    return matchingKeys;
  }

  public set(key: string, value: T) {
    if (!key)
      return;

    const cacheEntry = {
      timestamp: Date.now(),
      data: value
    };

    this.map.value.set(key, cacheEntry);
    triggerRef(this.map);

    return this;
  }

  public has(key: string) {
    return !!key && this.map.value.has(key);
  }

  public delete(key: string) {
    if (!key || !this.has(key)) {
      return false;
    }

    this.map.value.delete(key);
    triggerRef(this.map);

    return true;
  }

  public clear() {
    this.map.value.clear();
    triggerRef(this.map);
  }

  public* entries(): IterableIterator<[string, T]> {
    for (const [key, value] of this.map.value.entries()) {
      yield [key, value.data];
    }
  }

  protected entriesArray(): [string, CacheEntry<T>][] {
    return Array.from(this.map.value.entries());
  }

  public keys() {
    return this.map.value.keys();
  }

  public* values(): IterableIterator<T> {
    for (const value of this.map.value.values()) {
      yield value.data;
    }
  }

  public refreshTimestampForKey(key: string) {
    if (!key)
      return;

    const entry = this.map.value.get(key);

    if (!entry)
      return;

    entry.timestamp = Date.now();
  }

  public onBeforeDeletion(callback: (thisObj: this) => void) {
    this.beforeDeletionCallbacks.push(callback);

    return () => {
      this.beforeDeletionCallbacks = this.beforeDeletionCallbacks.filter(sub => sub !== callback);
    };
  }

  public onAfterUpdate(callback: () => any) {
    watch(this.map, callback);
  }

  public getComputedRefValueFromRefKey(reactiveKey: Ref<string | null>): ComputedRef<T | null>;
  public getComputedRefValueFromRefKey(reactiveKey: ComputedRef<string>): ComputedRef<T | null>;
  public getComputedRefValueFromRefKey(reactiveKey: Ref<string | null> | ComputedRef<string>): ComputedRef<T | null> {
    return computed(() => {
      if (!reactiveKey.value) {
        return null;
      }

      const cacheValue = this.map.value.get(reactiveKey.value);

      if (!cacheValue) {
        return null;
      }

      return cacheValue.data;
    });
  }

  public getComputedRefValuesFromRefKeys(reactiveKeys: Ref<string[]>): ComputedRef<(T|null)[]> {
    return computed(() => {
      const cacheValues: (T|null)[] = [];

      for (const key of reactiveKeys.value) {
        const cacheValue = this.map.value.get(key);

        if (!cacheValue) {
          cacheValues.push(null);
          continue;
        }

        cacheValues.push(cacheValue.data);
      }

      return cacheValues;
    });
  }

  public destroy() {
    window.clearInterval(this.intervalId);

    this.clear();
  }

  protected abstract deleteOutdatedEntries(): void;
}

export class SizeCacheMap<T> extends CacheMap<T> {
  private maxEntryCount: number;

  constructor(updateFrequency: number, maxEntryCount: number) {
    super(updateFrequency);

    this.maxEntryCount = maxEntryCount;
  }

  protected override deleteOutdatedEntries(): void {
    const size = this.size;

    if (size <= this.maxEntryCount)
      return;

    const entryCacheEntryArray = this.entriesArray();

    entryCacheEntryArray.sort((entryOne, entryTwo) => entryOne[1].timestamp - entryTwo[1].timestamp); // Oldest first

    const keysToDelete = entryCacheEntryArray.slice(0, size - this.maxEntryCount).map(entry => entry[0]);

    for (const key of keysToDelete) {
      this.delete(key);
    }
  }
}

export class TimeCacheMap<T> extends CacheMap<T> {
  private expireAfter: number;

  constructor(updateFrequency: number, expireAfter: number) {
    super(updateFrequency);

    this.expireAfter = expireAfter;
  }

  protected deleteOutdatedEntries(): void {
    const currentTime = Date.now();
    const keyValuePairs = this.entriesArray();

    for (const [key, value] of keyValuePairs) {
      if (currentTime - value.timestamp > this.expireAfter) {
        this.delete(key);
      }
    }
  }
}

export class PromiseCacheMap<T> extends CacheMap<Promise<T>> {
  constructor() {
    super(null);
  }

  override set(key: string, value: Promise<T>) {
    this.delete(key);

    super.set(key, value);

    value.finally(() => {
      // if there was a new promise of the same key set in the map, don't delete based on the callback for this one

      if (this.get(key) === value) {
        this.delete(key);
      }
    });

    return this;
  }

  protected deleteOutdatedEntries(): void { /* not needed */ }
}
